"use client";

import { memo, type FC } from "react";
import { Box } from "@ecco/ui/box";
import { Button } from "@ecco/ui/button";
import { ECCOIcon } from "@ecco/ui/ecco-icon";
import { Flex } from "@ecco/ui/flex";
import { useMicrocopy } from "@lib/contentful/microcopy/MicrocopyContext";
import CommonCMS from "@lib/constants/contentful/cms-common";

/** Highest possible z-index — ensures close button sits above Fibbl's QR overlay */
const Z_INDEX_MAX = 2147483647;

type FibblVtoButtonProps = {
    isVtoActive: boolean;
    isFibblReady: boolean;
    onOpen: () => void;
    onClose: () => void;
};

export const FibblVtoButton: FC<FibblVtoButtonProps> = memo(
    ({ isVtoActive, isFibblReady, onOpen, onClose }) => {
        const { get: getMicrocopy } = useMicrocopy();
        const buttonLabel = getMicrocopy(CommonCMS.shared, CommonCMS.fibbl.buttonLabel);

        if (!isFibblReady) return null;

        return (
            <>
                {isVtoActive && (
                    <Box
                        position="absolute"
                        top={0}
                        left={0}
                        right={0}
                        height="50%"
                        pointerEvents="none"
                        zIndex={Z_INDEX_MAX}
                    >
                        <Flex
                            position={{ base: "absolute", lg: "sticky" }}
                            top={0}
                            left={0}
                            right={0}
                            justifyContent="flex-end"
                            py={{ base: 2, lg: 16 }}
                            px={4}
                        >
                            <Button
                                onClick={onClose}
                                variant="unstyled"
                                aria-label="Close QR code"
                                pointerEvents="auto"
                            >
                                <ECCOIcon name="close" size="lg" color="white" />
                            </Button>
                        </Flex>
                    </Box>
                )}

                {!isVtoActive && (
                    <Box
                        position="absolute"
                        bottom={{ base: "6%", lg: "12%" }}
                        left="50%"
                        transform="translateX(-50%)"
                        display="block"
                        zIndex={Z_INDEX_MAX}
                    >
                        <Button
                            onClick={onOpen}
                            variant="primary"
                            px={{ base: 2, lg: 4 }}
                            py={0}
                            border="1px solid"
                            borderColor="blackAlpha.800"
                            backgroundColor="whiteAlpha.600"
                            backdropFilter="blur(8px)"
                            color="gray.800"
                            _hover={{ backgroundColor: "whiteAlpha.800" }}
                        >
                            <Flex
                                direction="row"
                                align="center"
                                justify="center"
                                gap={2}
                                width="100%"
                                height="100%"
                            >
                                <ECCOIcon name="virtual-try-on" size="md" />
                                {buttonLabel}
                                <ECCOIcon name="arrow-right" size="md" />
                            </Flex>
                        </Button>
                    </Box>
                )}
            </>
        );
    }
);
